import { PageSlugs } from "../../add-element/types";
import { IsPreviewOrDemoProvider } from "../../contexts/IsPreviewOrDemoContext";
import { isNewLayoutsEnabled } from "../../contexts/StaticSiteDataContext";
import { useCurrentSite } from "../../contexts/models/SiteContext";
import { RouteInfo, withInvalidRouteRedirect } from "../../core/routing";
import Paragraphs from "../../pages/website/Paragraphs";
import { WeddingSite } from "../../resources/modelTypes";
import { RootState } from "../../resources/reduxTypes";
import {
  getAllResources,
  getAllSections,
  getResource,
  filterPage,
} from "../selectors";
import FancyClothesProvider from "./FancyClothesProvider";
import Footer from "./Footer";
import WebsiteHeader from "./WebsiteHeader";
import WebsitePages from "./WebsitePages";
import { css, Global } from "@emotion/react";
import { GlobalStyles } from "@minted/minted-components";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import React, { memo } from "react";
import { useSelector } from "react-redux";

const WeddingWebsite = ({ route }: { route: RouteInfo }) => {
  const site = useCurrentSite() as WeddingSite;
  const pages = useSelector((state: RootState) =>
    getResource(state.resources.pages),
  );
  const resources = useSelector((state: RootState) =>
    getAllResources(state.resources),
  );
  const themeAuthor = useSelector(
    (state: RootState) => state.theme.attribution,
  );
  const sections = useSelector((state: RootState) =>
    getAllSections(state.resources),
  );

  const filteredPages = pages.filter((page) =>
    filterPage(page, {
      ...resources,
      sections,
      sites: [site],
    }),
  );

  let activePage = filteredPages.find((page) => page.slug === route.name);

  if (!activePage) {
    activePage = filteredPages.find((page) => page.slug === PageSlugs.Home);
  }

  const pagesToRender =
    site.pageConfiguration === "multi_page" ? [activePage] : filteredPages;

  // Removable once new-layouts is on for everyone: secondary pages share the home page's header there.
  const useCondensedHeader =
    !isNewLayoutsEnabled(site) &&
    site.pageConfiguration === "multi_page" &&
    activePage.slug !== PageSlugs.Home;

  const textAlignment = get(activePage, "configuration.textAlignment");

  const customCssElement = site.extraCss ? (
    <Global
      styles={css`
        ${site.extraCss}
      `}
    />
  ) : null;

  return (
    <FancyClothesProvider>
      <GlobalStyles />
      {customCssElement}
      <IsPreviewOrDemoProvider
        isDemo={Boolean(site.isDemoSite)}
        isPreview={false}
      >
        <div id="page-home">
          <WebsiteHeader condensed={useCondensedHeader} pages={filteredPages}>
            <Paragraphs
              text={site.siteDetails.welcomeText || ""}
              textAlignment={textAlignment}
            />
          </WebsiteHeader>
        </div>
        <WebsitePages
          pages={pagesToRender}
          resources={resources}
          site={site}
          source="website"
        />
        <Footer artist={themeAuthor} />
      </IsPreviewOrDemoProvider>
    </FancyClothesProvider>
  );
};

const MemoizedWeddingWebsite = memo(WeddingWebsite, isEqual);

// Apply route redirect handling
export default withInvalidRouteRedirect(MemoizedWeddingWebsite, {
  injectRoute: true,
  redirectTo: PageSlugs.Home,
});
